import React, { useContext, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { isBrowser, isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { UpgradeAccountContext } from '../../../../../contexts/settings/upgradeAccount/context';
import ModalRoot, { Modal, ModalBody, ModalHeader } from '../../../../../components/Modal';
import { GenericLoading } from '../../../../../components/Placeholders';
import {
  SETTINGS_TIER_STANDARD_ALL_KEY,
  USER_TIERS_RISK_ASSESSMENTS,
} from '../../../../../utilities/sharedQueries';
import useRiskTierSettings from '../../../../../utilities/sharedHooks/useRiskTierSettings';
import VerificationNotes from './VerificationNotes';

const MainLayout = ({ children = null, className = '' }) => {
  const {
    state,
    openScreen,
    closeVerificationNotes,
    updateTiersStandard,
    updateCurrentTier,
    updateNextTierInfo,
    updateUserRiskTier,
    updateSelectedSourceOfWealth,
    updateSelectedBusinessSector,
  } = useContext(UpgradeAccountContext);

  const history = useHistory();

  const { state: locationState } = useLocation();

  const { riskAssessment } = useRiskTierSettings();

  const { data: tiersStandard, isLoading: isLoadingStandard } = useQuery(
    SETTINGS_TIER_STANDARD_ALL_KEY
  );

  const { data: userTiersAssessmentsInfo, isLoading: isLoadingUserTiersRiskAssessments } = useQuery(
    USER_TIERS_RISK_ASSESSMENTS
  );

  const renderedComponent = useMemo(() => {
    if (
      isLoadingStandard ||
      isLoadingUserTiersRiskAssessments ||
      !riskAssessment?.userRisk ||
      !riskAssessment?.userNextTier
    ) {
      return <GenericLoading isFullScreen={false} />;
    }
    if (Array.isArray(children)) {
      return children?.filter((child) => child.props.keyName === state?.currentScreen);
    }
    return [children]?.filter((child) => child.props.keyName === state?.currentScreen);
  }, [
    children,
    state?.currentScreen,
    isLoadingStandard,
    riskAssessment,
    isLoadingUserTiersRiskAssessments,
  ]);

  useEffect(() => {
    if (!locationState?.initScreen) {
      history.push('/dashboard/settings');
    } else {
      openScreen(locationState?.initScreen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedSourceOfWealth = useMemo(() => {
    if (tiersStandard && userTiersAssessmentsInfo) {
      const sourceOfWealthOptions = tiersStandard?.filter(
        (item) => item.risk_type_key === 'source_of_wealth'
      );
      if (sourceOfWealthOptions) {
        const selectedOption = userTiersAssessmentsInfo.find(
          (item) => item.risk_type_key === 'source_of_wealth'
        );
        if (selectedOption) {
          return sourceOfWealthOptions.find((item) => item.key === selectedOption.user_input);
        }
      }
    }
    return null;
  }, [tiersStandard, userTiersAssessmentsInfo]);

  const selectedBusinessSector = useMemo(() => {
    if (tiersStandard && userTiersAssessmentsInfo) {
      const businessSectorOptions = tiersStandard?.filter(
        (item) => item.risk_type_key === 'business_sector'
      );
      if (businessSectorOptions) {
        const selectedOption = userTiersAssessmentsInfo.find(
          (item) => item.risk_type_key === 'business_sector'
        );
        if (selectedOption) {
          return businessSectorOptions.find((item) => item.key === selectedOption.user_input);
        }
      }
    }
    return null;
  }, [tiersStandard, userTiersAssessmentsInfo]);

  useEffect(() => {
    if (tiersStandard) {
      updateTiersStandard(tiersStandard);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiersStandard]);

  useEffect(() => {
    if (riskAssessment) {
      updateCurrentTier(riskAssessment?.userRisk?.key);
      updateNextTierInfo(riskAssessment?.userNextTier);
      updateUserRiskTier(riskAssessment?.userRisk);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskAssessment]);

  useEffect(() => {
    if (selectedSourceOfWealth && !state.selectedSourceOfWealth) {
      updateSelectedSourceOfWealth(selectedSourceOfWealth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSourceOfWealth]);

  useEffect(() => {
    if (selectedBusinessSector && !state.selectedBusinessSector) {
      updateSelectedBusinessSector(selectedBusinessSector);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessSector]);

  const closeUpgradeAccount = () => history.push('/dashboard/settings');

  return (
    <div className="d-flex flex-row main-layout">
      {isBrowser && !state.verificationNoteOpened && (
        <div
          className="position-absolute"
          onClick={closeUpgradeAccount}
          onKeyDown={closeUpgradeAccount}
          role="button"
          style={{ top: '4%', right: '3%', zIndex: 100 }}
          tabIndex={-1}>
          <i
            className="close-btn cursor-pointer icon icon-close-other text-dark text-f4"
            aria-label="Close"
          />
        </div>
      )}
      <div className="d-flex flex-column flex-fill align-items-center h-100vh position-relative">
        <div
          className={`d-flex flex-column w-100 h-100 position-relative ${className} ${
            isMobile ? 'px-3' : ''
          }`}
          style={{
            paddingTop: isBrowser ? '6rem' : '2rem',
            maxWidth: '33.5rem',
            transitionProperty: 'margin',
            transitionDuration: '300ms',
          }}>
          {renderedComponent}
        </div>
      </div>
      {isBrowser && (
        <div
          className={`cryptodraft-sidebar bg-lighter ${
            state.verificationNoteOpened ? 'py-3 px-4 cryptodraft-sidebar-expanded' : ''
          }`}>
          <div className="d-flex flex-row ps-4 pt-4">
            <i
              onClick={closeVerificationNotes}
              onKeyDown={closeVerificationNotes}
              role="button"
              className="close-btn cursor-pointer icon icon-close-other text-dark text-f4"
              tabIndex={-1}
              aria-label="Close"
            />
          </div>
          <VerificationNotes />
        </div>
      )}
      {isMobile && state.verificationNoteOpened && (
        <ModalRoot>
          <Modal specialMobileContentClass="w-auto rounded-0 rounded-top py-3 px-0">
            <ModalHeader>
              <i
                onClick={closeVerificationNotes}
                onKeyDown={closeVerificationNotes}
                role="button"
                className="close-btn cursor-pointer icon icon-close-other text-shadow"
                tabIndex={-1}
                aria-label="Close"
              />
            </ModalHeader>
            <ModalBody>
              <VerificationNotes />
            </ModalBody>
          </Modal>
        </ModalRoot>
      )}
    </div>
  );
};

export default MainLayout;

MainLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  className: PropTypes.string,
};
